// IndexedDB-backed BrokerStore for contexts that don't have
// localStorage. The whole reason this file exists: SharedWorker
// global scope exposes IndexedDB but NOT localStorage, so the
// `LocalStorageBrokerStore` that ships in `./store.ts` silently
// no-ops when constructed inside the SharedWorker — every write
// goes to a null storage and every read on the next worker boot
// finds nothing. Hence studies / sessions / events appearing to
// "wipe" in IPC mode.
//
// Persistence shape mirrors LocalStorageBrokerStore: three keys
// under one prefix, holding the JSON-serialized snapshot, event
// tail, and resource table respectively. Load returns a Promise
// (the BrokerStore contract accepts either sync or async load);
// save / appendEvent / saveResource / deleteResource are
// fire-and-forget — the broker's in-memory state is the source of
// truth, and IDB is just there for "survive a worker restart."
//
// Single-object-store layout (key:value), all reads / writes
// going through one transaction. We don't shard by type because
// the broker's traffic shape is small (low-thousands of bytes per
// write for a typical session), and a single store keeps the
// transaction count low.

import type { BrokerSnapshot, EventLogRow, ResourceRow, SessionExport } from './types';
import { MemoryBrokerStore } from './store';

const SNAPSHOT_KEY = 'snapshot';
const EVENTS_KEY = 'events';
const RESOURCES_KEY = 'resources';

export interface IDBBrokerStoreOptions {
  /** IndexedDB database name. Defaults to `helm-broker`. */
  dbName?: string;
  /** Object store name. Defaults to `state`. */
  storeName?: string;
  /** Cap on retained event-tail length. Defaults to 5000. */
  maxEvents?: number;
}

export class IDBBrokerStore extends MemoryBrokerStore {
  private readonly dbName: string;
  private readonly storeName: string;
  private readonly maxEvents: number;
  private dbPromise: Promise<IDBDatabase | null>;

  constructor(opts: IDBBrokerStoreOptions = {}) {
    super();
    this.dbName = opts.dbName ?? 'helm-broker';
    this.storeName = opts.storeName ?? 'state';
    this.maxEvents = opts.maxEvents ?? 5000;
    this.dbPromise = openDb(this.dbName, this.storeName);
  }

  /**
   * Load every persisted blob, hydrate the in-memory shadow, and
   * return the snapshot. Resources land via `loadResources`, which
   * the broker awaits separately. Returns a vanilla empty snapshot
   * if IDB is unavailable (test contexts, some private-browsing
   * profiles) so the broker still boots.
   */
  override load(): Promise<BrokerSnapshot | null> {
    return (async () => {
      const db = await this.dbPromise;
      if (!db) return super.load();
      try {
        const [snapRaw, eventsRaw] = await Promise.all([
          idbGet<string>(db, this.storeName, SNAPSHOT_KEY),
          idbGet<string>(db, this.storeName, EVENTS_KEY),
        ]);
        if (typeof snapRaw === 'string') {
          try {
            const parsed = JSON.parse(snapRaw) as BrokerSnapshot;
            if (parsed && parsed.version === 1) {
              // Replace the parent's shadow so subsequent
              // synchronous load() callers (none today, but the
              // contract allows it) get this snapshot.
              (this as unknown as { snapshot: BrokerSnapshot }).snapshot = parsed;
            }
          } catch {
            // ignore corrupted snapshot
          }
        }
        if (typeof eventsRaw === 'string') {
          try {
            const parsed = JSON.parse(eventsRaw) as EventLogRow[];
            if (Array.isArray(parsed)) {
              (this as unknown as { eventLog: EventLogRow[] }).eventLog = parsed;
            }
          } catch {
            // ignore
          }
        }
        return super.load();
      } catch {
        return super.load();
      }
    })();
  }

  override loadResources(): Promise<ResourceRow[]> {
    return (async () => {
      const db = await this.dbPromise;
      if (!db) return super.loadResources();
      try {
        const raw = await idbGet<string>(db, this.storeName, RESOURCES_KEY);
        if (typeof raw !== 'string') return super.loadResources();
        const parsed = JSON.parse(raw) as ResourceRow[];
        if (!Array.isArray(parsed)) return super.loadResources();
        // Seed the parent's in-memory resource map so subsequent
        // sync calls reflect the persisted state immediately.
        for (const row of parsed) {
          if (row && typeof row.resource === 'string' && typeof row.id === 'string') {
            super.saveResource(row);
          }
        }
        return super.loadResources();
      } catch {
        return super.loadResources();
      }
    })();
  }

  override save(snapshot: BrokerSnapshot): void {
    super.save(snapshot);
    void this.persistKey(SNAPSHOT_KEY, JSON.stringify(snapshot));
  }

  override appendEvent(row: EventLogRow): void {
    super.appendEvent(row);
    const tail = this.allEvents().slice(-this.maxEvents);
    void this.persistKey(EVENTS_KEY, JSON.stringify(tail));
  }

  override saveResource(row: ResourceRow): void {
    super.saveResource(row);
    void this.persistKey(RESOURCES_KEY, JSON.stringify(super.loadResources()));
  }

  override deleteResource(resource: string, id: string): void {
    super.deleteResource(resource, id);
    void this.persistKey(RESOURCES_KEY, JSON.stringify(super.loadResources()));
  }

  // exportSession is inherited as-is — it reads from the parent's
  // in-memory snapshot + eventLog, which the load() override
  // populated. No IDB round-trip needed.

  private async persistKey(key: string, value: string): Promise<void> {
    const db = await this.dbPromise;
    if (!db) return;
    try {
      await idbPut(db, this.storeName, key, value);
    } catch {
      // Quota / aborted transaction / private browsing.
      // In-memory state still holds; we'll try again on the next
      // write.
    }
  }
}

function openDb(name: string, storeName: string): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(name, 1);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

function idbGet<T>(
  db: IDBDatabase,
  storeName: string,
  key: string,
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(
  db: IDBDatabase,
  storeName: string,
  key: string,
  value: unknown,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    tx.onabort = () => reject(tx.error);
  });
}

export type { SessionExport };
